import type React from "react";

export interface Position {
  parentX: number;
  parentY: number;
  childX: number;
  childY: number;
}

interface Props extends Position {
  offsetX?: number;
  active?: boolean;
}

const borderWidth = 4;

const Line: React.FC<Props> = ({
  parentX,
  parentY,
  childX,
  childY,
  offsetX,
  active,
}) => {
  const width = Math.abs(parentX - childX) + borderWidth;
  const left = Math.min(parentX, childX) + (offsetX ?? 0) - borderWidth / 2;

  const top = Math.min(parentY, childY);
  const height = childY - parentY;

  // Use a tolerance so sub-pixel differences from getBoundingClientRect()
  // don't produce a narrow S-shape instead of a straight vertical line.
  const EPSILON = 1;
  const childToTheRightOfParent = childX - parentX > EPSILON;
  const childToTheLeftOfParent = parentX - childX > EPSILON;
  const childBelowParent = !childToTheRightOfParent && !childToTheLeftOfParent;

  const borderColor = active ? "#16a34a" : "#d1d5db";
  const boxHeight = height / 2 + borderWidth / 2;

  return (
    <div
      style={{
        position: "absolute",
        display: "flex",
        flex: 1,
        top,
        left,
        width,
        height,
        borderColor,
        borderLeftWidth: childBelowParent ? borderWidth : 0,
        zIndex: active ? 1 : 0,
      }}
    >
      {/* Left box */}
      <div
        style={{
          display: "flex",
          flex: 1,
          alignSelf: childToTheRightOfParent ? "flex-start" : "flex-end",
          height: boxHeight,
          borderColor,
          borderLeftWidth: !childBelowParent ? borderWidth : 0,
          // box is placed in top left corner
          borderBottomLeftRadius: childToTheRightOfParent ? 10 : 0,
          borderBottomWidth: childToTheRightOfParent ? borderWidth : 0,
          // box is placed in bottom left corner
          borderTopLeftRadius: childToTheLeftOfParent ? 10 : 0,
          borderTopWidth: childToTheLeftOfParent ? borderWidth : 0,
        }}
      />
      {/* Right box */}
      <div
        style={{
          display: "flex",
          flex: 1,
          alignSelf: childToTheRightOfParent ? "flex-end" : "flex-start",
          height: boxHeight,
          borderColor,
          borderRightWidth: !childBelowParent ? borderWidth : 0,
          // box is placed in top right corner
          borderBottomRightRadius: childToTheLeftOfParent ? 10 : 0,
          borderBottomWidth: childToTheLeftOfParent ? borderWidth : 0,
          // box is placed in bottom right corner
          borderTopRightRadius: childToTheRightOfParent ? 10 : 0,
          borderTopWidth: childToTheRightOfParent ? borderWidth : 0,
        }}
      />
    </div>
  );
};

export default Line;
